import { isBlobNamespace } from '../../namespaces.js';
import { recordKey } from '../local/local-key-encoding.js';
export function getDesktopBridgeStorage() {
    if (typeof window === 'undefined')
        return null;
    const bridge = window.skjvDesktop;
    return bridge?.storage ?? null;
}
function toBase64(bytes) {
    let bin = '';
    for (let i = 0; i < bytes.length; i += 0x8000) {
        bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    }
    return btoa(bin);
}
function fromBase64(b64) {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++)
        bytes[i] = bin.charCodeAt(i);
    return bytes.buffer;
}
/**
 * SKJV Desktop provider: same envelopes as the localStorage provider, but
 * persisted as one JSON file per record under the user's storage root
 * (~/.skjv/data by default) through the preload bridge (PRD §5a). Registered
 * under the 'local' provider id, so existing config enables it untouched.
 */
export class DesktopDiskProvider {
    id = 'local';
    displayName = 'This computer';
    bridge;
    constructor(bridge) {
        const resolved = bridge ?? getDesktopBridgeStorage();
        if (!resolved)
            throw new Error('desktop bridge storage is not available');
        this.bridge = resolved;
    }
    async isReady() {
        return true;
    }
    async getRecord(ns, key) {
        const doc = await this.bridge.get(ns, key);
        if (!doc || typeof doc !== 'object')
            return null;
        const rec = doc;
        if (rec.sidecars)
            delete rec.sidecars;
        return rec;
    }
    /** Disk is authoritative; localStorage doubles as the synchronous boot
     *  mirror the web app's sync read path (local-sync.ts) reads. The preload
     *  hydrates it from disk; writes here keep it fresh within the session. */
    mirror(ns, key, envelope) {
        try {
            localStorage.setItem(recordKey(ns, key), JSON.stringify(envelope));
        }
        catch {
            // Mirror-only failure — disk already has the record.
        }
    }
    async putRecord(rec) {
        // Sidecars (Blobs) are derived data — never serialized (same rule as local).
        const { sidecars: _ignored, ...raw } = rec;
        // The preload bridge structured-clones arguments; MobX observables (and
        // any proxy) fail that clone. Envelopes are small JSON — plain-ify.
        const persisted = JSON.parse(JSON.stringify(raw));
        await this.bridge.put(rec.namespace, rec.key, persisted);
        this.mirror(rec.namespace, rec.key, persisted);
    }
    async deleteRecord(ns, key, deletedAt) {
        const existing = await this.getRecord(ns, key);
        const tombstone = {
            namespace: ns,
            key,
            value: existing?.value ?? null,
            updatedAt: existing?.updatedAt ?? deletedAt,
            deletedAt,
            version: existing?.version,
            // Keep the folder identity — without these a folder-layout record
            // would jump to a UUID-named folder the moment it is tombstoned.
            displayName: existing?.displayName,
            recordFileName: existing?.recordFileName,
        };
        await this.bridge.put(ns, key, tombstone);
        this.mirror(ns, key, tombstone);
    }
    async listRefs(ns) {
        const keys = await this.bridge.list(ns);
        const refs = [];
        for (const key of keys) {
            const rec = await this.getRecord(ns, key);
            if (!rec)
                continue;
            refs.push({
                namespace: rec.namespace,
                key: rec.key,
                updatedAt: rec.updatedAt,
                deletedAt: rec.deletedAt,
            });
        }
        return refs;
    }
    async getBlob(ns, key) {
        if (!isBlobNamespace(ns))
            return null;
        const doc = (await this.bridge.get(ns, key));
        if (!doc?.__blob)
            return null;
        return new Blob([fromBase64(doc.__blob)], { type: doc.contentType });
    }
    async putBlob(ns, key, blob, updatedAt) {
        if (!isBlobNamespace(ns))
            return;
        const bytes = new Uint8Array(await blob.arrayBuffer());
        const doc = {
            __blob: toBase64(bytes),
            contentType: blob.type || 'application/octet-stream',
            updatedAt,
        };
        await this.bridge.put(ns, key, doc);
    }
    /** Sidecars (PDFs) are regenerated from envelopes — not persisted here. */
    async getSidecar() {
        return null;
    }
}
//# sourceMappingURL=desktop-disk-provider.js.map